import type { CreateScheduledDeliveryLinePayload, TypedMoneyBasis } from "../api/types";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A hand-typed delivery line, as the editor holds it — and the ONE place it
 * becomes a payload, for both the new-delivery page and the edit.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Until G-121 S3 each page built its own payload, and they had drifted: the
 * new-delivery page refused an empty unit cost, the edit sent it as €0
 * (`Number("")`). With S3 an empty cost also has no side of IVA, so the two
 * would have drifted further; one builder now serves both.
 */

export interface DeliveryLineDraft {
  uid: string;
  itemId: string;
  zoneId: string;
  expectedQty: string;
  expectedUnitCostEur: string;
  /**
   * G-121 S3 (OD-138): which side of IVA the unit cost is on — null until the
   * person picks (a cost the app pre-filled from a stored price starts on
   * Before IVA, see `utils/typedMoneyBasis.ts`).
   */
  moneyBasis: TypedMoneyBasis | null;
}

export function newDeliveryLineDraft(): DeliveryLineDraft {
  return {
    uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    itemId: "",
    zoneId: "",
    expectedQty: "1",
    expectedUnitCostEur: "",
    moneyBasis: null,
  };
}

/** The payload for line `index` (0-based), or an Error naming what is missing. */
export function deliveryLinePayload(
  line: DeliveryLineDraft,
  index: number,
): CreateScheduledDeliveryLinePayload {
  const n = index + 1;
  if (!line.itemId) throw new Error(`Line ${n}: pick an item`);
  if (!line.zoneId) throw new Error(`Line ${n}: pick a zone`);
  const qty = Number(line.expectedQty);
  if (!Number.isFinite(qty) || qty <= 0) {
    throw new Error(`Line ${n}: expected qty must be a positive number`);
  }
  const price = Number(line.expectedUnitCostEur);
  if (line.expectedUnitCostEur.trim() === "" || !Number.isFinite(price) || price < 0) {
    throw new Error(`Line ${n}: unit cost must be a non-negative number`);
  }
  if (line.moneyBasis === null) {
    throw new Error(`Line ${n}: say whether the unit cost is before IVA or includes IVA`);
  }
  return {
    itemId: line.itemId,
    zoneId: line.zoneId,
    expectedQty: qty,
    expectedUnitCostEur: price,
    moneyBasis: line.moneyBasis,
  };
}

/**
 * The draft's expected total BEFORE IVA, while every priced line is typed
 * before IVA — quantity × unit, the preview it always was. Once any priced
 * line includes IVA (or has no side yet), the before-IVA figure is the
 * server's to work out, so this answers null and the page says so: the
 * browser does no IVA arithmetic (see `utils/priceFloor.ts`).
 */
export function typedLinesTotal(lines: DeliveryLineDraft[]): number | null {
  let total = 0;
  for (const l of lines) {
    const qty = Number(l.expectedQty);
    const price = Number(l.expectedUnitCostEur);
    if (l.expectedUnitCostEur.trim() === "" || !Number.isFinite(qty) || !Number.isFinite(price)) continue;
    if (l.moneyBasis !== "BEFORE_IVA") return null;
    total += qty * price;
  }
  return total;
}
